import _ from 'lodash'

import { getServiceWrapper } from 'core/service-wrapper'
import { partnerApiUtils } from 'core/service-wrapper/partner-api/handler-request/service-utils'

/**
 * key mappings between eSub app doc to partner-api
 * @type {{ [key: string]: { to_key: string, transformForward?: (value) => any, transformBackward?: (value) => any } }}
 */
const KEY_MAPPINGS = {
  'quickQuote.biProps.birthdate': {
    to_key: 'quickQuote.biProps.data.insureds[0].birthDate',
    transformForward: (value) => partnerApiUtils.birthdate.toApi(value),
    transformBackward: (value) => partnerApiUtils.birthdate.toFrontEnd(value),
  },
  'quickQuote.biProps.distributorType': 'quickQuote.biProps.data.sellerInformation[0].distributorGroup',
  'quickQuote.biProps.insured.age.value': 'quickQuote.biProps.data.insureds[0].age.value',
  'quickQuote.biProps.productCategory': 'quickQuote.biProps.data.category',
  'quickQuote.biProps.selectedModelFactorLabel': {
    to_key: 'quickQuote.biProps.data.paymentPeriodCode',
    transformForward: (value) => partnerApiUtils.modalFactor.toLabel(value),
    transformBackward: (value) => partnerApiUtils.modalFactor.toModalFactorId(value),
  },
  'quickQuote.biProps.paymentModeLabel': {
    to_key: 'quickQuote.biProps.data.paymentPeriodCode',
    transformForward: (value) => partnerApiUtils.modalFactor.toLabel(value),
    transformBackward: (value) => partnerApiUtils.modalFactor.toModalFactorId(value),
  },
  'quickQuote.biProps.totalPremium': 'quickQuote.biProps.data.totalPremium',
  'quickQuote.biProps.allocations': {
    to_key: 'quickQuote.biProps.data.insureds[0].fundsInfo',
    transformBackward: (value) => {
      return (value || []).map((fund) => ({
        code: fund.fundCode,
        selected: true,
        fundAllocation: fund.fundPercentage,
      }))
    },
  },
}

/**
 *
 * @param key
 * @returns {[string, (function(value): any) | undefined, (function(value): any) | undefined]}
 */
const getKeyMapping = (key, prefix) => {
  let f_key = _.compact([prefix, key]).join('.')
  if (KEY_MAPPINGS[f_key]) {
    const obj = KEY_MAPPINGS[f_key]
    let to_key, transformForward, transformBackward

    if (typeof obj === 'string') {
      to_key = obj
    } else {
      to_key = obj.to_key
      transformForward = obj.transformForward
      transformBackward = obj.transformBackward
    }

    if (prefix && to_key.startsWith(prefix)) {
      to_key = to_key.replace(prefix, '')
    }
    if (to_key.startsWith('.')) {
      to_key = to_key.replace('.', '')
    }
    return [to_key, transformForward, transformBackward]
  }
  return [key, null, null]
}

const appDoc = (usePartnerApi) => {
  if (!usePartnerApi) {
    return {
      toKey: (key, prefix) => key,
      toKeyValue: (key, value) => [key, value],
      get: (app, key, prefix, defaultValue) => _.get(app, key, defaultValue),
    }
  }

  const toKey = (key, prefix) => {
    const [to_key] = getKeyMapping(key, prefix)
    return to_key
  }

  /**
   * Transform key and value according to mapping rules.
   * using:
   *  - on setAppValue (yield put(setAppValue(...biProps.transform('quickQuote.biProps.birthdate', value))))
   * @param key {string}
   * @param value {any}
   */
  const toKeyValue = (key, value, prefix) => {
    const [to_key, transform] = getKeyMapping(key, prefix)
    let t_value = value
    if (transform) {
      t_value = transform(value)
    }
    return [to_key, t_value]
  }

  const get = (app, key, prefix, defaultValue) => {
    const [to_key, transformForward, transformBackward] = getKeyMapping(key, prefix)
    let t_value = _.get(app, to_key, defaultValue)
    if (transformBackward && t_value !== undefined) {
      t_value = transformBackward(t_value)
    }
    return t_value
  }

  return {
    toKey,
    toKeyValue,
    get,
  }
}

export const appDocKey = (key, prefix = '') => {
  const serviceWrapper = getServiceWrapper()
  return appDoc(serviceWrapper.isCurrentProductUsePartnerApi()).toKey(key, prefix)
}

export const appDocsGet = (key, prefix = '') => (app) => {
  const serviceWrapper = getServiceWrapper()
  return appDoc(serviceWrapper.isCurrentProductUsePartnerApi()).get(app, key, prefix, undefined)
}

export const appDocsPut = (key, value, prefix = '') => {
  const serviceWrapper = getServiceWrapper()
  return appDoc(serviceWrapper.isCurrentProductUsePartnerApi()).toKeyValue(key, value, prefix)
}
